import React, { useState, useEffect } from 'react';
import { getAllSlides, addSlide, updateSlide, deleteSlide } from '../../utils/firestore';
import { uploadImage } from '../../utils/imageUpload';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

const ManageSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formType, setFormType] = useState('create'); // 'create' or 'edit'
  const { register, handleSubmit, formState: { errors }, reset, setValue } = useForm();

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const slidesData = await getAllSlides();
        setSlides(slidesData);
      } catch (error) {
        console.error('Error fetching slides:', error);
        toast.error('Failed to fetch slides');
      } finally {
        setLoading(false);
      }
    };

    fetchSlides();
  }, []);

  // Set form values when editing a slide
  useEffect(() => {
    if (editingSlide) {
      setValue('title', editingSlide.title);
      setValue('subtitle', editingSlide.subtitle);
      setValue('active', editingSlide.active);
      setValue('order', editingSlide.order || 0);
    } else {
      reset();
    }
  }, [editingSlide, reset, setValue]);

  const onSubmit = async (data) => {
    try {
      if (formType === 'create') {
        // Handle image upload
        let imageUrl = '';
        if (data.image && data.image[0]) {
          imageUrl = await uploadImage(data.image[0], 'slider-images');
        }
        
        // Create slide
        const slideData = {
          title: data.title,
          subtitle: data.subtitle,
          image: imageUrl,
          active: data.active === 'true', // Convert string to boolean
          order: parseInt(data.order) || 0,
          createdAt: new Date()
        };
        
        await addSlide(slideData);
        toast.success('Slide created successfully');
      } else {
        // Handle image upload if new image provided
        let imageUrl = editingSlide.image;
        if (data.image && data.image[0]) {
          imageUrl = await uploadImage(data.image[0], 'slider-images');
        }
        
        // Update slide
        const slideData = {
          title: data.title,
          subtitle: data.subtitle,
          image: imageUrl,
          active: data.active === 'true', // Convert string to boolean
          order: parseInt(data.order) || 0
        };
        
        await updateSlide(editingSlide.id, slideData);
        toast.success('Slayt başarıyla güncellendi.');
      }
      
      // Refresh data and reset form
      const slidesData = await getAllSlides();
      setSlides(slidesData);
      setShowForm(false);
      setEditingSlide(null);
      reset();
    } catch (error) {
      console.error('Error saving slide:', error);
      toast.error('Failed to save slide');
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide);
    setFormType('edit');
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu slaytı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      try {
        await deleteSlide(id);
        const slidesData = await getAllSlides();
        setSlides(slidesData);
        toast.success('Slayt başarıyla silindi.');
      } catch (error) {
        console.error('Error deleting slide:', error);
        toast.error('Failed to delete slide');
      }
    }
  };

  const toggleActive = async (slide) => {
    try {
      await updateSlide(slide.id, { ...slide, active: !slide.active });
      const slidesData = await getAllSlides();
      setSlides(slidesData);
      toast.success(`Slide ${slide.active ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      console.error('Error updating slide status:', error);
      toast.error(`Failed to ${slide.active ? 'deactivate' : 'activate'} slide`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nihal-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Slaytları Yönet</h1>
        <button
          onClick={() => {
            setFormType('create');
            setEditingSlide(null);
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          Yeni Slayt Ekle
        </button>
      </div>

      {showForm ? (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            {formType === 'create' ? 'Yeni Slayt Ekle' : 'Slayt Güncelle'}
          </h2>
          
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Başlık *
                </label>
                <input
                  id="title"
                  type="text"
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  {...register('title', { required: 'Başlık zorunlu' })}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
                  Görüntüleme Sırası
                </label>
                <input
                  id="order"
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  {...register('order')}
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="subtitle" className="block text-sm font-medium text-gray-700 mb-1">
                Alt Başlık
              </label>
              <textarea
                id="subtitle"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                {...register('subtitle')}
              ></textarea>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
                  Görsel
                </label>
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  {...register('image')}
                />
                {editingSlide?.image && !register('image').value && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">Varsıyalan Görsel:</p>
                    <img 
                      src={editingSlide.image} 
                      alt="Current" 
                      className="w-32 h-32 object-cover mt-2 rounded"
                    />
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="active" className="block text-sm font-medium text-gray-700 mb-1">
                  Statü
                </label>
                <select
                  id="active"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  {...register('active')}
                >
                  <option value="true">Aktif</option>
                  <option value="false">İnaktif</option>
                </select>
              </div>
            </div>
            
            <div className="flex space-x-4">
              <button
                type="submit"
                className="btn btn-primary"
              >
                {formType === 'create' ? 'Slayt Oluştur' : 'Slayt Güncelle'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingSlide(null);
                }}
                className="btn btn-secondary"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      ) : null}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Slayt Görseli
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Başlık ve Alt Yazı
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Görüntüleme Sırası
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Statü
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Eylemler
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {slides.map((slide) => (
              <tr key={slide.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {slide.image ? (
                    <img 
                      src={slide.image} 
                      alt={slide.title} 
                      className="w-16 h-16 object-cover rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-gray-500 text-xs">Resim Yok</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{slide.title}</div>
                  <div className="text-sm text-gray-500">{slide.subtitle}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {slide.order}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span 
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      slide.active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {slide.active ? 'Aktif' : 'İnaktif'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => toggleActive(slide)}
                    className={`mr-4 ${
                      slide.active 
                        ? 'text-red-600 hover:text-red-900' 
                        : 'text-green-600 hover:text-green-900'
                    }`}
                  >
                    {slide.active ? 'İnaktif' : 'Aktif'}
                  </button>
                  <button
                    onClick={() => handleEdit(slide)}
                    className="text-blue-600 hover:text-indigo-900 mr-4"
                  >
                    Düzenle
                  </button>
                  <button
                    onClick={() => handleDelete(slide.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Sil
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        
        {slides.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Slayt bulunamadı. İlk slaytını oluştur!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageSlides;